import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ScanLine, Search, RefreshCw, AlertTriangle, X, CheckCircle, List, Package, Plus, User } from 'lucide-react';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import ClientListGrid, { useClientListGrid } from '../ui/ClientListGrid';

const NOTE_COLUMNS = [
  { field: 'date', title: 'Date', widthClass: 'w-[10%]', hideable: true },
  { field: 'refno', title: 'Ref No', widthClass: 'w-[12%]', hideable: true },
  { field: 'status', title: 'Status', widthClass: 'w-[10%]', hideable: true },
  { field: 'remarks', title: 'Remarks', widthClass: 'w-[14%]', hideable: true },
  { field: 'driver', title: 'Driver', widthClass: 'w-[14%]', hideable: true },
  { field: 'vehicle', title: 'Vehicle', widthClass: 'w-[14%]', hideable: true },
  { field: 'createdby', title: 'Created By', widthClass: 'w-[13%]', hideable: true },
  { field: 'updatedby', title: 'Updated By', widthClass: 'w-[13%]', hideable: true },
];

const DETAIL_COLUMNS = [
  { field: 'voucher', title: 'Voucher No', widthClass: 'w-[12%]', hideable: true },
  { field: 'clientbill', title: 'Client Bill', widthClass: 'w-[12%]', hideable: true },
  { field: 'client', title: 'Client', widthClass: 'w-[16%]', hideable: true },
  { field: 'clientaddress', title: 'Client Address', widthClass: 'w-[20%]', hideable: true },
  { field: 'customer', title: 'Customer', widthClass: 'w-[16%]', hideable: true },
  { field: 'customeraddress', title: 'Customer Address', widthClass: 'w-[20%]', hideable: true },
];

const formatNoteDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateString;
  }
};

const formatAddress = (addr) =>
  [
    addr?.Location?.Name,
    addr?.Location?.Area?.Name,
    addr?.Emirate?.Name,
  ].filter(Boolean).join(', ') || '-';

const getNoteCellValue = (row, field) => {
  switch (field) {
    case 'date':
      return formatNoteDate(row.Date);
    case 'refno':
      return row.RefNo || '-';
    case 'status':
      return row.Status || '-';
    case 'remarks':
      return row.Remarks || '-';
    case 'driver':
      return row.Driver?.Name || '-';
    case 'vehicle': {
      const number = row.Vehicle?.Number || '-';
      return row.Vehicle?.Model ? `${number}, ${row.Vehicle.Model}` : number;
    }
    case 'createdby':
      return row.CreatedName || '-';
    case 'updatedby':
      return row.UpdatedName || '-';
    default:
      return row[field];
  }
};

const getDetailCellValue = (row, field) => {
  const order = row.Order || {};
  switch (field) {
    case 'voucher':
      return order.VoucherNo || '-';
    case 'clientbill':
      return order.ClientBillNo || '-';
    case 'client':
      return `${order.Client?.Name || '-'}, ${order.Client?.MobileNo1 || ''}`;
    case 'clientaddress':
      return formatAddress(order.ClientAddress);
    case 'customer':
      return `${order.Customer?.Name || '-'}, ${order.Customer?.MobileNo1 || ''}`;
    case 'customeraddress':
      return formatAddress(order.CustomerAddress);
    default:
      return row[field];
  }
};

const DeliveryScan = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedDeliveryNote, setSelectedDeliveryNote] = useState(null);
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [deliveryNoteDetails, setDeliveryNoteDetails] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [voucherScanInput, setVoucherScanInput] = useState('');
  const [newDeliveryNoteModalOpen, setNewDeliveryNoteModalOpen] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [driverSearchTerm, setDriverSearchTerm] = useState('');

  useEffect(() => {
    loadDeliveryNotes();
  }, []);

  useEffect(() => {
    if (selectedDeliveryNote?.Id) {
      loadDeliveryNoteDetails();
    }
  }, [selectedDeliveryNote]);

  // Toast notification effect
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadDeliveryNotes = async () => {
    try {
      setLoadingNotes(true);
      const response = await MainService.GetNewDeliveryNotes();
      setDeliveryNotes(response || []);
    } catch (err) {
      console.error('Load delivery notes error:', err);
      setError('Failed to load delivery notes');
      setDeliveryNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const loadDeliveryNoteDetails = async () => {
    if (!selectedDeliveryNote?.Id) return;
    try {
      setLoading(true);
      const response = await MainService.GetDeliveryDetailsListById(selectedDeliveryNote.Id);
      setDeliveryNoteDetails(response || []);
    } catch (err) {
      console.error('Load delivery note details error:', err);
      setError('Failed to load delivery note details');
      setDeliveryNoteDetails([]);
    } finally {
      setLoading(false);
    }
  };

  const handleScanVoucher = async () => {
    if (!voucherScanInput.trim()) {
      setError('Enter voucher number');
      return;
    }
    if (!selectedDeliveryNote?.Id) {
      setError('Select Delivery Note');
      return;
    }
    try {
      setLoading(true);
      // First, get the order by voucher number
      const trackResponse = await MainService.TrackOrdersByVoucherNo({
        VoucherNos: [voucherScanInput.trim()]
      });
      
      if (!trackResponse || trackResponse.length === 0) {
        setError('Order not found for this voucher number');
        setVoucherScanInput('');
        return;
      }

      const order = trackResponse[0];
      if (!order.Id) {
        setError('Invalid order data');
        setVoucherScanInput('');
        return;
      }

      // Add scanned order to delivery note using the new API
      const payload = {
        OrderId: order.Id,
        DeliveryNoteId: selectedDeliveryNote.Id,
      };
      const resp = await MainService.AddScannedOrderToDN(payload);
      if (resp === 'Success' || resp === true || resp === 'true') {
        setSuccess('Order added successfully');
        setVoucherScanInput('');
        await loadDeliveryNoteDetails();
      } else {
        setError(resp || 'Failed to add order');
        setVoucherScanInput('');
      }
    } catch (err) {
      console.error('Scan voucher error:', err);
      setError('Network Error');
      setVoucherScanInput('');
    } finally {
      setLoading(false);
    }
  };

  const handleVoucherScanKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleScanVoucher();
    }
  };

  const handleFinish = async () => {
    if (!selectedDeliveryNote?.Id) {
      setError('Select Delivery Note');
      return;
    }
    if (deliveryNoteDetails.length === 0) {
      setError('No orders in delivery note. Please scan at least one order.');
      return;
    }
    try {
      setLoading(true);
      const currentDate = new Date();
      const orderIds = deliveryNoteDetails
        .map(detail => detail.Order?.Id)
        .filter(id => id != null);

      if (orderIds.length === 0) {
        setError('No valid orders found in delivery note');
        return;
      }

      const payload = {
        Id: selectedDeliveryNote.Id,
        DriverId: selectedDeliveryNote.DriverId || selectedDeliveryNote.Driver?.Id || null,
        VehicleId: selectedDeliveryNote.VehicleId || selectedDeliveryNote.Vehicle?.Id || null,
        Day: currentDate.getDate(),
        Month: currentDate.getMonth() + 1, // JavaScript months are 0-indexed
        Year: currentDate.getFullYear(),
        OrderIds: orderIds,
      };

      const resp = await MainService.ConfirmDeliveryNote(payload);
      console.log('ConfirmDeliveryNote response:', resp);
      
      // Handle different response formats: wrapped in Data, direct object, or string
      const responseData = resp?.Data || resp;
      const status = responseData?.Status || (typeof responseData === 'string' && responseData === 'Success' ? 'Success' : null);
      const isSuccess = status === 'Success' || resp === 'Success' || resp === true || resp === 'true' || responseData === 'Success';
      
      console.log('Is Success:', isSuccess, 'Status:', status);
      
      if (isSuccess) {
        const deliveryNoteId = responseData?.DeliveryNoteId || resp?.DeliveryNoteId || selectedDeliveryNote.Id;
        console.log('Delivery Note ID for print:', deliveryNoteId);
        setSuccess('Delivery note confirmed successfully');
        
        // Print delivery note allocation
        try {
          console.log('Calling PrintDeliveryNoteAllocation with ID:', deliveryNoteId);
          const printResponse = await MainService.PrintDeliveryNoteAllocation(deliveryNoteId);
          console.log('PrintDeliveryNoteAllocation response:', printResponse);
          
          if (printResponse) {
            // Handle both string and object responses
            const fileName = typeof printResponse === 'string' 
              ? printResponse 
              : (printResponse?.Data || printResponse?.data || printResponse);
            
            if (fileName) {
              const pdfUrl = `${getApiBaseUrl()}ReportFiles/${fileName}`;
              console.log('Opening PDF URL:', pdfUrl);
              window.open(pdfUrl, '_blank');
            } else {
              console.error('No file name in print response');
            }
          } else {
            console.error('Empty print response');
          }
        } catch (printErr) {
          console.error('Print error:', printErr);
          setError('Failed to generate PDF report');
        }
        
        // Reset and reload
        setTimeout(() => {
          setSelectedDeliveryNote(null);
          setDeliveryNoteDetails([]);
          setVoucherScanInput('');
          loadDeliveryNotes();
        }, 2000);
      } else {
        const errorMsg = responseData?.Status || resp?.Status || (typeof resp === 'string' ? resp : 'Failed to confirm delivery note');
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Finish error:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDeliveryNote = (note) => {
    setSelectedDeliveryNote(note);
    setVoucherScanInput('');
  };

  const handleOpenNewDeliveryNoteModal = async () => {
    setNewDeliveryNoteModalOpen(true);
    setDriverSearchTerm('');
    await loadDrivers();
  };

  const loadDrivers = async () => {
    try {
      setLoadingDrivers(true);
      const response = await MainService.DriversInDriverAllocation();
      setDrivers(response || []);
    } catch (err) {
      console.error('Load drivers error:', err);
      setError('Failed to load drivers');
      setDrivers([]);
    } finally {
      setLoadingDrivers(false);
    }
  };

  const handleDriverClick = (driver) => {
    if (driver.OrderCount > 0) {
      setError('Driver already has an open delivery note');
      return;
    }
    setSelectedDriver(driver);
    setConfirmDialogOpen(true);
  };

  const handleConfirmCreateDeliveryNote = async () => {
    if (!selectedDriver?.Id) {
      setError('No driver selected');
      return;
    }
    try {
      setLoading(true);
      const payload = {
        DriverId: selectedDriver.Id,
      };
      const resp = await MainService.CreateDeliveryNote(payload);
      
      // Handle different response formats
      const responseData = resp?.Data || resp;
      const status = responseData?.Status || resp?.Status;
      
      if (status === 'Success' || resp === 'Success') {
        setSuccess('Delivery note created successfully');
        setNewDeliveryNoteModalOpen(false);
        setConfirmDialogOpen(false);
        setSelectedDriver(null);
        await loadDeliveryNotes();
      } else {
        const errorMsg = status || (typeof resp === 'string' ? resp : 'Failed to create delivery note');
        setError(errorMsg);
        setConfirmDialogOpen(false);
      }
    } catch (err) {
      console.error('Create delivery note error:', err);
      setError('Network Error');
      setConfirmDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const filteredDrivers = drivers.filter(driver => {
    if (!driverSearchTerm) return true;
    const searchLower = driverSearchTerm.toLowerCase();
    return (
      (driver.Name || '').toLowerCase().includes(searchLower) ||
      (driver.MobileNo || '').toLowerCase().includes(searchLower)
    );
  });

  const filteredDeliveryNotes = useMemo(() => deliveryNotes.filter(note =>
    !searchKeyword ||
    (note.RefNo || '').toLowerCase().includes(searchKeyword.toLowerCase()) ||
    (note.Driver?.Name || '').toLowerCase().includes(searchKeyword.toLowerCase()) ||
    (note.Vehicle?.Number || '').toLowerCase().includes(searchKeyword.toLowerCase())
  ), [deliveryNotes, searchKeyword]);

  const noteColumns = useMemo(() => NOTE_COLUMNS, []);
  const detailColumns = useMemo(() => DETAIL_COLUMNS, []);
  const notesGrid = useClientListGrid({ columns: noteColumns, rows: filteredDeliveryNotes, getCellValue: getNoteCellValue });
  const detailsGrid = useClientListGrid({ columns: detailColumns, rows: deliveryNoteDetails, getCellValue: getDetailCellValue });

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center mb-2">
              <Package className="w-6 h-6 text-primary mr-2" />
              <h1 className="text-3xl font-bold text-gray-900">Delivery Scan</h1>
            </div>
            <p className="text-sm text-gray-500 ml-8">Push orders to delivery notes</p>
          </div>
          <button
            onClick={handleOpenNewDeliveryNoteModal}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Delivery Note
          </button>
        </div>
      </div>

      {/* Delivery Notes List - Shown First, Hidden when selected */}
      {!selectedDeliveryNote && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex flex-col">
              <div className="flex items-center mb-1">
                <List className="w-5 h-5 text-primary mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Delivery Notes</h3>
              </div>
              <p className="text-sm text-gray-500 ml-7">Choose a delivery note first to do delivery scan</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by Ref No, Driver, or Vehicle..."
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary w-64"
                />
              </div>
              <button
                onClick={loadDeliveryNotes}
                className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>

          <ClientListGrid
            columns={noteColumns}
            grid={notesGrid}
            loading={loadingNotes}
            loadingText="Loading delivery notes..."
            emptyText="No delivery notes found"
            minWidth="1100px"
            rowKey={(row, index) => row.Id || index}
            onRowClick={handleSelectDeliveryNote}
            renderCell={(row, column) => {
              if (column.field === 'refno') {
                return <span className="font-semibold text-gray-900">{getNoteCellValue(row, column.field)}</span>;
              }
              if (column.field === 'vehicle') {
                return (
                  <>
                    {row.Vehicle?.Number || '-'}
                    {row.Vehicle?.Model && <span className="text-gray-500">, {row.Vehicle.Model}</span>}
                  </>
                );
              }
              return getNoteCellValue(row, column.field);
            }}
          />
        </div>
      )}

      {/* Selected Delivery Note Info */}
      {selectedDeliveryNote && (
        <div className="bg-primary bg-opacity-10 border border-primary rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm">
              <div>
                <span className="text-gray-600 font-medium">Selected Delivery Note:</span>
                <span className="font-semibold text-gray-900 ml-2">{selectedDeliveryNote.RefNo || 'N/A'}</span>
              </div>
              <div className="text-gray-400">|</div>
              <div>
                <span className="text-gray-600 font-medium">Driver:</span>
                <span className="font-semibold text-gray-900 ml-2">{selectedDeliveryNote.Driver?.Name || 'N/A'}</span>
              </div>
              <div className="text-gray-400">|</div>
              <div>
                <span className="text-gray-600 font-medium">Date:</span>
                <span className="font-semibold text-gray-900 ml-2">{formatNoteDate(selectedDeliveryNote.Date)}</span>
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedDeliveryNote(null);
                setDeliveryNoteDetails([]);
                setVoucherScanInput('');
              }}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors text-sm"
            >
              Change Delivery Note
            </button>
          </div>
        </div>
      )}

      {/* Delivery Note Details Table - Shown when selected */}
      {selectedDeliveryNote && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Orders in Delivery Note</h3>
            <div className="flex items-center gap-3">
              <div className="relative">
                <ScanLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Scan Voucher No..."
                  value={voucherScanInput}
                  onChange={(e) => setVoucherScanInput(e.target.value)}
                  onKeyPress={handleVoucherScanKeyPress}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary w-64"
                  disabled={loading}
                />
              </div>
              <button
                onClick={handleScanVoucher}
                disabled={loading || !voucherScanInput.trim()}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <ScanLine className="w-4 h-4" />
                )}
                Add
              </button>
              <button
                onClick={handleFinish}
                disabled={loading || deliveryNoteDetails.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Finish
              </button>
            </div>
          </div>
          <ClientListGrid
            columns={detailColumns}
            grid={detailsGrid}
            loading={loading}
            loadingText="Loading..."
            emptyText="No orders in this delivery note. Scan a voucher to add orders."
            minWidth="1100px"
            rowKey={(row, index) => row.Id || row.Order?.Id || index}
            renderCell={(row, column) => getDetailCellValue(row, column.field)}
          />
        </div>
      )}

      {/* New Delivery Note Modal */}
      {newDeliveryNoteModalOpen && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Select Driver</h2>
                <button
                  onClick={() => {
                    setNewDeliveryNoteModalOpen(false);
                    setSelectedDriver(null);
                    setDriverSearchTerm('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by driver name or mobile number..."
                  value={driverSearchTerm}
                  onChange={(e) => setDriverSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {loadingDrivers ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-primary animate-spin" />
                </div>
              ) : filteredDrivers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {drivers.length === 0 ? 'No drivers available' : 'No drivers found matching your search'}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredDrivers.map((driver) => {
                    const hasOpenNote = driver.OrderCount > 0;
                    return (
                      <div
                        key={driver.Id}
                        onClick={() => !hasOpenNote && handleDriverClick(driver)}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          hasOpenNote
                            ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                            : 'border-gray-200 hover:border-primary hover:shadow-md cursor-pointer bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`p-3 rounded-full ${hasOpenNote ? 'bg-gray-200' : 'bg-primary bg-opacity-10'}`}>
                              <User className={`w-6 h-6 ${hasOpenNote ? 'text-gray-400' : 'text-primary'}`} />
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">{driver.Name || 'N/A'}</h3>
                              {driver.MobileNo && (
                                <p className="text-sm text-gray-500">{driver.MobileNo}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Order Count:</span>
                            <span className={`font-semibold ${hasOpenNote ? 'text-orange-600' : 'text-green-600'}`}>
                              {driver.OrderCount || 0}
                            </span>
                          </div>
                          {hasOpenNote && (
                            <p className="text-xs text-orange-600 mt-2">
                              Already has an open delivery note
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Confirmation Dialog */}
      {confirmDialogOpen && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-primary bg-opacity-10 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Confirm Creation</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Do you want to create new delivery note for driver <strong>{selectedDriver?.Name || 'N/A'}</strong>?
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setConfirmDialogOpen(false);
                    setSelectedDriver(null);
                  }}
                  disabled={loading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmCreateDeliveryNote}
                  disabled={loading}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Confirm'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DeliveryScan;
